/**
 * Contact Form Enhancement
 * Handles floating labels, textarea auto-resize and loading states
 */

class ContactForm {
	constructor() {
		this.init();
	}

	init() {
		this.setupTextareaAutoResize();
		this.setupFormSubmission();
		this.setupFloatingLabels();
	}

	/**
	 * Auto-resize textarea based on content
	 */
	setupTextareaAutoResize() {
		const textareas = document.querySelectorAll(
			'textarea[id="ContactForm-body"]'
		);

		textareas.forEach((textarea) => {
			// Initial height adjustment
			this.adjustTextareaHeight(textarea);

			// Listen for input events
			textarea.addEventListener("input", () => {
				this.adjustTextareaHeight(textarea);
			});

			// Listen for focus/blur for better UX
			textarea.addEventListener("focus", () => {
				this.adjustTextareaHeight(textarea);
			});
		});
	}

	/**
	 * Adjust textarea height based on content
	 */
	adjustTextareaHeight(textarea) {
		// Reset height to auto to get the actual scrollHeight
		textarea.style.height = "auto";

		// Set minimum height
		const minHeight = 120; // matches min-h-[120px] in CSS
		const maxHeight = 300; // reasonable max height

		// Calculate new height
		const newHeight = Math.max(
			minHeight,
			Math.min(maxHeight, textarea.scrollHeight)
		);

		// Apply new height
		textarea.style.height = newHeight + "px";
	}

	/**
	 * Setup floating label behavior for better browser compatibility
	 */
	setupFloatingLabels() {
		const inputs = document.querySelectorAll(
			"#ContactForm input, #ContactForm textarea"
		);

		inputs.forEach((input) => {
			// Check if input has value on page load
			this.updateLabelState(input);

			// Listen for various events
			input.addEventListener("input", () => {
				this.updateLabelState(input);
			});

			input.addEventListener("blur", () => {
				this.updateLabelState(input);
			});

			input.addEventListener("focus", () => {
				this.updateLabelState(input);
			});
		});
	}

	/**
	 * Update label state based on input value
	 */
	updateLabelState(input) {
		// Use stable parent-child relationship instead of CSS classes
		const fieldContainer = input.parentElement;
		const label = fieldContainer?.querySelector(`label[for="${input.id}"]`);

		if (!fieldContainer || !label) return;

		// Add/remove active class based on input value or focus
		if (input.value.trim() !== "" || document.activeElement === input) {
			label.classList.add(
				"bx--translate-y-6",
				"bx-scale-75",
				"bx-text-theme-black"
			);
		} else {
			label.classList.remove(
				"bx--translate-y-6",
				"bx-scale-75",
				"bx-text-theme-black"
			);
		}
	}

	/**
	 * Enhanced form submission with loading states
	 */
	setupFormSubmission() {
		const contactForm = document.getElementById("ContactForm");
		const contactSubmitBtn = contactForm?.querySelector(
			'button[type="submit"]'
		);

		if (contactForm && contactSubmitBtn) {
			contactForm.addEventListener("submit", (e) => {
				// Add loading state when form is submitted
				contactSubmitBtn.disabled = true;
				contactSubmitBtn.textContent = "Sending...";

				// Store original button text if not already stored
				if (!contactSubmitBtn.getAttribute("data-original-text")) {
					contactSubmitBtn.setAttribute(
						"data-original-text",
						contactSubmitBtn.textContent
					);
				}
			});

			// Reset button state on page load if there are server errors
			// Check for Shopify form error messages instead of CSS classes
			const errorContainer =
				contactForm.parentElement.querySelector('[class*="bg-red"]');
			if (errorContainer) {
				contactSubmitBtn.disabled = false;
				contactSubmitBtn.textContent =
					contactSubmitBtn.getAttribute("data-original-text") ||
					"Send";
			}
		}

		// Handle newsletter forms in footer
		this.setupNewsletterFormSubmission();
	}

	/**
	 * Setup newsletter form submission with loading states
	 */
	setupNewsletterFormSubmission() {
		// Handle newsletter forms (both in footer and modal)
		// Look for all forms and check if they are newsletter forms
		const allForms = document.querySelectorAll("form");

		allForms.forEach((form) => {
			// Check if it's a newsletter form by looking for email input and tags hidden field
			const hasEmailInput = form.querySelector(
				'input[type="email"][name="contact[email]"]'
			);
			const hasTagsInput = form.querySelector(
				'input[name="contact[tags]"]'
			);

			if (hasEmailInput && hasTagsInput) {
				const submitBtn = form.querySelector('button[type="submit"]');
				const submitText = submitBtn?.querySelector(".submit-text");
				const loadingSpinner =
					submitBtn?.querySelector(".loading-spinner");

				if (submitBtn && submitText && loadingSpinner) {
					// Only add event listener if not already added
					if (!form.hasAttribute("data-newsletter-handled")) {
						form.setAttribute("data-newsletter-handled", "true");

						form.addEventListener("submit", (e) => {
							// Disable button and show loading spinner
							submitBtn.disabled = true;
							submitBtn.classList.add("bx-cursor-not-allowed");

							// Animate loading state
							this.animateNewsletterLoading(
								submitText,
								loadingSpinner,
								true
							);
						});
					}
				}
			}
		});

		// Reset newsletter form states on page load/errors
		this.resetNewsletterFormStates();
	}

	/**
	 * Animate newsletter form loading state
	 */
	animateNewsletterLoading(submitText, loadingSpinner, isLoading) {
		if (isLoading) {
			// Hide submit text and show loading spinner
			submitText.style.opacity = "0";
			loadingSpinner.style.opacity = "1";
		} else {
			// Show submit text and hide loading spinner
			submitText.style.opacity = "1";
			loadingSpinner.style.opacity = "0";
		}
	}

	/**
	 * Reset newsletter form states on page load
	 */
	resetNewsletterFormStates() {
		// Find all newsletter forms and reset their states
		const allForms = document.querySelectorAll("form");

		allForms.forEach((form) => {
			const hasEmailInput = form.querySelector(
				'input[type="email"][name="contact[email]"]'
			);
			const hasTagsInput = form.querySelector(
				'input[name="contact[tags]"]'
			);

			if (hasEmailInput && hasTagsInput) {
				const submitBtn = form.querySelector('button[type="submit"]');
				const submitText = submitBtn?.querySelector(".submit-text");
				const loadingSpinner =
					submitBtn?.querySelector(".loading-spinner");

				if (submitBtn && submitText && loadingSpinner) {
					// Reset button state
					submitBtn.disabled = false;
					submitBtn.classList.remove("bx-cursor-not-allowed");

					// Reset to normal state
					this.animateNewsletterLoading(
						submitText,
						loadingSpinner,
						false
					);
				}
			}
		});
	}
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
	new ContactForm();
});

// Initialize if DOM is already loaded
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", () => {
		new ContactForm();
	});
} else {
	new ContactForm();
}
